// client/src/pages/RightPane.js
import {
  Box,
  Flex,
  IconButton,
  Text,
  Button,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Textarea,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { Download, CheckCircle2, Circle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import SuggestionsDialog from '../components/SuggestionsDialog';
import LoadingState from '../components/LoadingState';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { downloadPDFFromCache, savePDFToCache } from '../utils/pdf_cache_setup';

export default function RightPane({
  onClose,
  onScroll,
  typingSuggestionId,
  rightPaneRef,
  content,
  extractedSuggestions,
  selectedSuggestions,
  onConfirmSelection,
  onGenerateReport,
  turnId,
  typingReportId,
  isLoadingSuggestions,
  isLoadingReport,
  onSubmitFeedback,
  reportLoadingPhase,
  feedbackState,
  setFeedbackState,
  onEnsureExtractedSuggestions,
  suggestionExtractionStatus,
}) {


  const API_BASE_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
  const isSuggestionsView = content?.type === 'suggestions';
  const isReportView = content?.type === 'report';
  const showLoadingState =
    (isLoadingSuggestions && typingSuggestionId === turnId) ||
    (isLoadingReport && typingReportId?.turnId === turnId && typingReportId?.version === content?.version);

  // ── Auto-scroll for streaming content ──
  const contentContainerRef = useRef(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const isStreamingContent =
    (typingSuggestionId === turnId && content?.type === 'suggestions') ||
    (typingReportId?.turnId === turnId && typingReportId?.version === content?.version && content?.type === 'report');

  const handleContentScroll = useCallback((e) => {
    const el = e.currentTarget;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    setUserScrolledUp(!isAtBottom);
    if (onScroll) onScroll(e);
  }, [onScroll]);

  useEffect(() => {
    if (isStreamingContent && !userScrolledUp && contentContainerRef.current) {
      contentContainerRef.current.scrollTop = contentContainerRef.current.scrollHeight;
    }
  }, [content?.content, isStreamingContent, userScrolledUp]);

  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const [isSuggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Tab state (0 = Report, 1 = Selections)
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Feedback state management
  const [feedbackData, setFeedbackData] = useState({
    contentId: null,
    feedbackType: null,
    comment: '',
    isRemoving: false
  });
  const { isOpen: isFeedbackOpen, onOpen: onFeedbackOpen, onClose: onFeedbackClose } = useDisclosure();

  // Get the turn ID used as the feedback key
  const feedbackTurnId = isSuggestionsView ? turnId : content?.turnId;

  // Handle feedback icon click
  const handleFeedbackClick = (feedbackType) => {
    if (!feedbackTurnId) return;

    // Read current feedback from the unified state
    const currentType = currentFeedback?.type;

    if (currentType === feedbackType) {
      setFeedbackData({
        contentId: feedbackTurnId,
        feedbackType: null,
        comment: currentFeedback?.comment || '',
        isRemoving: true,
      });
    } else {
      setFeedbackData({
        contentId: feedbackTurnId,
        feedbackType,
        comment: currentFeedback?.comment || '',
        isRemoving: false,
      });
    }

    onFeedbackOpen();
  };


  // Handle feedback submission
  const handleFeedbackSubmit = () => {
    const { contentId: fidTurnId, feedbackType, comment, isRemoving } = feedbackData;
    const newFeedback = isRemoving
      ? { type: null, comment: '' }
      : { type: feedbackType, comment: comment || '' };

    // Write to unified feedback state
    setFeedbackState(prev => ({ ...prev, [fidTurnId]: newFeedback }));

    // Call unified API
    if (onSubmitFeedback) {
      onSubmitFeedback(fidTurnId, isRemoving ? null : feedbackType, isRemoving ? '' : comment || '');
    }

    onFeedbackClose();
    setFeedbackData({
      contentId: null,
      feedbackType: null,
      comment: '',
      isRemoving: false
    });
  };

  // Read from unified feedback state
  const currentFeedback = feedbackState?.[feedbackTurnId];

  const isThumbsUpActive = currentFeedback?.type === 'positive';
  const isThumbsDownActive = currentFeedback?.type === 'negative';


  let loadingTitle = '';
  let loadingDescription = '';
  let view = '';

  if (isLoadingSuggestions && typingSuggestionId === turnId) {
    loadingTitle = 'Analyzing project details...';
    loadingDescription = 'Generating optimization suggestions based on your design...';
    view = 'optimizations';
  } else if (isLoadingReport && typingReportId?.turnId === turnId && typingReportId?.version === content?.version) {
    loadingTitle = 'Generating Report...';
    // Dynamic description based on phase:
    if (reportLoadingPhase === 'measurements') {
      loadingDescription = 'Fetching structural measurements from your design...';
    } else if (reportLoadingPhase === 'costs') {
      loadingDescription = 'Calculating material costs and quantities...';
    } else {
      loadingDescription = 'Creating comprehensive report with selected optimizations...';  // Fallback
    }
    view = 'report';
  }

  // Determine if typing indicator should show for report
  const isTypingReport = typingReportId?.turnId === turnId && typingReportId?.version === content?.version;

  // Check if any generation/streaming is in progress
  const isGenerating =
    (typingSuggestionId === turnId) || // This turn's suggestions are streaming/loading
    (typingReportId?.turnId === turnId && typingReportId?.version === content?.version); // This turn's report is streaming/loading

  const hasExtractedSuggestions = Object.keys(extractedSuggestions || {}).length > 0;
  const extractionState = suggestionExtractionStatus || 'idle';
  const canReviewSuggestions = !isGenerating && extractionState !== 'loading';

  const handleReviewSuggestionsClick = async () => {
    setReviewError('');

    if (hasExtractedSuggestions) {
      setSuggestionDialogOpen(true);
      return;
    }

    setSuggestionDialogOpen(true);
    setIsReviewLoading(true);

    try {
      await onEnsureExtractedSuggestions?.(turnId);
    } catch (err) {
      console.error('Failed to load review suggestions:', err);
      setReviewError('Failed to load optimization suggestions. Please try again.');
    } finally {
      setIsReviewLoading(false);
    }
  };

  const handleGenerateReportClick = () => {
    const checkedCount = selectedSuggestions?.length || 0;
    if (checkedCount === 0) {
      setConfirmDialogOpen(true);
    } else {
      onGenerateReport(turnId);
    }
  };

  // Confirm and proceed with report generation
  const confirmGenerateReport = () => {
    setConfirmDialogOpen(false);
    onGenerateReport(turnId);
  };

  const handleDownloadPDF = async () => {
    try {

      // ✅ FIRST: Try to get PDF from cache
      await downloadPDFFromCache(content?.turnId || turnId, content?.version || 1);

    } catch (error) {
      console.warn('⚠️ PDF not in cache, generating fresh...', error);

      // ❌ FALLBACK: If not in cache, generate fresh PDF
      try {
        const response = await fetch(`${API_BASE_URL}/pdf/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            markdown: content?.content || '',
            title: 'Alpherra Report',
            version: content?.version || 1
          })
        });

        if (!response.ok) throw new Error('PDF generation failed');

        const blob = await response.blob();

        // ✅ IMPORTANT: Cache the freshly generated PDF
        try {
          await savePDFToCache(
            content?.turnId || turnId,
            content?.version || 1,
            blob
          );
          console.log('✅ Freshly generated PDF cached successfully');
        } catch (cacheError) {
          console.warn('⚠️ Failed to cache PDF, but proceeding with download:', cacheError);
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Alpherra_Report_v${content?.version || 1}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

      } catch (innerError) {
        console.error('Error downloading PDF:', innerError);
        alert('Failed to generate PDF. Please try again.');
      }
    }
  };

  // Get selections data for the Selections tab

  const allSuggestions = extractedSuggestions || {};
  // 🔧 For reports, use the selections saved in the report object
  // For suggestions view, use current selections (which will be empty after report generation)
  const selectedKeys = content?.type === 'report' && content?.selectedSuggestions
    ? content.selectedSuggestions
    : (selectedSuggestions || []);

  const suggestionEntries = Object.entries(allSuggestions);
  return (
    <Flex
      direction="column"
      h="100%"
      w="100%"
      bg="white"
      border="1px solid #ECECEC"
      boxSizing="border-box"
      borderRadius="12px"
    // m={2}
    >


      {/* HEADER WITH TABS IN SAME LINE */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        flexShrink="0"
        py="12px"
        pr="16px"
        pl="16px"
        borderBottom="1px solid"
        borderColor="gray.200"
      >

        {/* LEFT: Title for Suggestions OR Tabs for Report */}
        {isSuggestionsView && !showLoadingState && (
          <Text fontSize="xl" fontWeight="semibold" color="#242424">
            Results
          </Text>
        )}

        {/* LEFT: Tabs (only show in report view) */}
        {isReportView && !showLoadingState && (
          <Flex gap="8px" align="center">
            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 0 ? '#CF008A' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(0)}
              _hover={{
                color: '#CF008A',
                bg: 'rgba(207, 0, 138, 0.05)'
              }}
              _after={activeTabIndex === 0 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #CF008A 0%, #037A9B 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Report
            </Button>

            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 1 ? '#037A9B' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(1)}
              _hover={{
                color: '#037A9B',
                bg: 'rgba(3, 122, 155, 0.05)'
              }}
              _after={activeTabIndex === 1 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #037A9B 0%, #00C2E5 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Selections {selectedKeys.length > 0 && `(${selectedKeys.length})`}
            </Button>
          </Flex>
        )}

        {/* RIGHT: Action Buttons */}
        <Flex gap="12px" align="center" ml="auto">

          {/* Feedback Icons - Show when content is visible (not loading) */}
          {!showLoadingState && (
            <>
              <IconButton
                icon={<ThumbsUp size={18} />}
                onClick={() => handleFeedbackClick('positive')}
                minW="unset"
                h="32px"
                w="32px"
                p={0}
                _hover={{ bg: '#F3F3F3' }}
                bg={isThumbsUpActive ? '#E8F5E9' : 'transparent'}
                color={isThumbsUpActive ? '#4CAF50' : '#666'}
                aria-label="Thumbs up"
                variant="ghost"
              />

              <IconButton
                icon={<ThumbsDown size={18} />}
                onClick={() => handleFeedbackClick('negative')}
                minW="unset"
                h="32px"
                w="32px"
                p={0}
                _hover={{ bg: '#F3F3F3' }}
                bg={isThumbsDownActive ? '#FFEBEE' : 'transparent'}
                color={isThumbsDownActive ? '#F44336' : '#666'}
                aria-label="Thumbs down"
                variant="ghost"
              />

              <Box w="1px" h="24px" bg="#E0E0E0" mx={1} />
            </>
          )}

          {/* Review Suggestions Button - Only in suggestions view */}
          {isSuggestionsView && !showLoadingState && (
            <Button
              leftIcon={
                <Box
                  width="16px"
                  height="16px"
                  backgroundImage="url('/icon_btn.svg')"
                  backgroundSize="contain"
                  backgroundRepeat="no-repeat"
                  backgroundPosition="center"
                  className="review-icon"
                />
              }
              size="sm"
              // onClick={() => setSuggestionDialogOpen(true)}
              onClick={handleReviewSuggestionsClick}
              px={2}
              bg="#FFFFFF"
              color="#037A9B"
              border="1px solid #00C2E5"
              borderRadius="12px"
              fontWeight="600"
              _hover={{
                bg: '#ffffff',
                color: '#CF008A',
                borderColor: '#FF9CEE',
                '& .review-icon': {
                  backgroundImage: "url('/icon_btn_pink.svg')"
                }
              }}
              _active={{ bg: '#FFFFFF' }}
              _disabled={{
                bg: '#F5F5F5',
                color: '#A0A0A0',
                borderColor: '#D0D0D0',
                opacity: 0.6,
                '& .review-icon': {
                  backgroundImage: "url('/icon_btn.svg')",
                  opacity: 0.5
                }
              }}
              isDisabled={!canReviewSuggestions}
              opacity={canReviewSuggestions ? 1 : 0.5}
            >
              Review Suggestions
              {selectedSuggestions && selectedSuggestions.length > 0 ? ` (${selectedSuggestions.length})` : ''}
            </Button>
          )}

          {/* Download Report Button - Only when Report tab is active */}
          {isReportView && !showLoadingState && activeTabIndex === 0 && (
            <Button
              leftIcon={<Download size={16} />}
              size="sm"
              // px="14px"
              onClick={handleDownloadPDF}
              bg="#CF008A"
              color="#FFFFFF"
              borderRadius="12px"
              _hover={{ bg: "#D933A1" }}
              _active={{ bg: "#CF008A" }}
              isDisabled={isGenerating}
              opacity={isGenerating ? 0.5 : 1}
              _disabled={{
                bg: '#F5F5F5',
                color: '#A0A0A0',
                borderColor: '#D0D0D0',
                opacity: 0.6
              }}
            >
              Download Report
            </Button>
          )}

          <IconButton
            size="sm"
            icon={<CloseIcon w="12px" h="12px" />}
            variant="ghost"
            onClick={onClose}
            aria-label="Close suggestions"
          />
        </Flex>
      </Flex>

      {/* CONTENT */}
      <Box
        position="relative"
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        p="16px"
        fontSize="14px"
        ref={contentContainerRef}
        onScroll={handleContentScroll}
        css={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#D0D0D0 transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#D0D0D0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#B0B0B0',
          },
        }}
      >
        {showLoadingState && (
          <Box>
            <LoadingState
              title={loadingTitle}
              description={loadingDescription}
              view={view}
            />
          </Box>
        )}

        {/* Suggestions View - No tabs */}
        {!showLoadingState && isSuggestionsView && (
          <Box
            // whiteSpace="pre-wrap"
            wordBreak="break-word"
            ref={rightPaneRef}
            onScroll={onScroll}
          >
            <MarkdownRenderer content={content.content} />
          </Box>
        )}

        {/* Report View - Content based on active tab */}
        {!showLoadingState && isReportView && (
          <Box>
            {activeTabIndex === 0 ? (
              // Report Tab Content
              <Box
                // whiteSpace="pre-wrap"
                wordBreak="break-word"
                ref={rightPaneRef}
                onScroll={onScroll}
              >
                <MarkdownRenderer content={content.content} />
              </Box>
            ) : (
              <>
                {selectedKeys.length === 0 ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    h="400px"
                    color="#999"
                  >
                    <Text fontSize="16px" fontWeight="500">
                      No optimization suggestions were selected
                    </Text>
                  </Flex>
                ) : (
                  <VStack spacing="16px" align="stretch" w="100%">
                    {/* Header Summary */}
                    {selectedKeys.length > 0 && (
                      <Box
                        mt="8px"
                        p="14px"
                        bg="rgba(3, 122, 155, 0.05)"
                        borderRadius="10px"
                        border="1px solid rgba(3, 122, 155, 0.15)"
                      >
                        <Flex align="center" gap="8px">
                          <CheckCircle2 size={18} color="#037A9B" />
                          <Text fontSize="13px" color="#037A9B" fontWeight="600">
                            {selectedKeys.length} of {suggestionEntries.length} optimizations included in this report
                          </Text>
                        </Flex>
                      </Box>
                    )}
                    {/* Suggestions List */}
                    <VStack spacing="10px" align="stretch">
                      {suggestionEntries.map(([key, suggestion], index) => {
                        const isSelected = selectedKeys.includes(key);

                        // Parse title and description from suggestion object or string
                        const title = typeof suggestion === 'object' ? suggestion.title :
                          key.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ');
                        const description = typeof suggestion === 'object' ? suggestion.description : suggestion;

                        return (
                          <Box
                            key={key}
                            p="16px"
                            borderRadius="12px"
                            bg={isSelected ? 'rgba(3, 122, 155, 0.04)' : '#FAFAFA'}
                            border="2px solid"
                            borderColor={isSelected ? '#037A9B' : '#E8E8E8'}
                            transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
                            position="relative"
                            overflow="hidden"
                            _hover={{
                              borderColor: isSelected ? '#037A9B' : '#D0D0D0',
                              transform: 'translateY(-2px)',
                              boxShadow: isSelected
                                ? '0 8px 16px rgba(3, 122, 155, 0.12)'
                                : '0 4px 12px rgba(0, 0, 0, 0.06)'
                            }}
                          >
                            {/* Left accent bar for selected items */}
                            {isSelected && (
                              <Box
                                position="absolute"
                                left="0"
                                top="0"
                                bottom="0"
                                w="4px"
                                bg="linear-gradient(180deg, #037A9B 0%, #00C2E5 100%)"
                              />
                            )}

                            <Flex align="flex-start" gap="14px">
                              {/* Icon */}
                              <Box
                                flexShrink={0}
                                mt="2px"
                                p="4px"
                                borderRadius="8px"
                                // bg={isSelected ? 'rgba(3, 122, 155, 0.1)' : 'transparent'}
                                transition="all 0.2s"
                              >
                                {isSelected ? (
                                  <CheckCircle2
                                    size={22}
                                    color="#037A9B"
                                    strokeWidth={2.5}
                                  />
                                ) : (
                                  <Circle
                                    size={22}
                                    color="#BEBEBE"
                                    strokeWidth={2}
                                  />
                                )}
                              </Box>

                              {/* Content */}
                              <Flex direction="column" flex="1" gap="8px">
                                {/* Number and Title */}
                                <Flex align="center" gap="10px">
                                  {/* <Badge
                                    fontSize="11px"
                                    fontWeight="700"
                                    px="8px"
                                    py="2px"
                                    borderRadius="6px"
                                    bg={isSelected ? '#037A9B' : '#D0D0D0'}
                                    color="white"
                                    textTransform="uppercase"
                                    letterSpacing="0.5px"
                                  >
                                    {String(index + 1).padStart(2, '0')}
                                  </Badge> */}
                                  <Text
                                    fontSize="15px"
                                    fontWeight="700"
                                    color={isSelected ? '#037A9B' : '#242424'}
                                    lineHeight="1.4"
                                    transition="color 0.2s"
                                  >
                                    {title}
                                  </Text>
                                </Flex>

                                {/* Description */}
                                <Text
                                  fontSize="14px"
                                  color="#555"
                                  lineHeight="1.7"
                                  pl="2px"
                                >
                                  {description}
                                </Text>

                                {/* Selected Badge */}
                                {/* {isSelected && (
                                  <Flex align="center" gap="6px" mt="4px">
                                    <Box
                                      w="8px"
                                      h="8px"
                                      borderRadius="50%"
                                      bg="#037A9B"
                                      boxShadow="0 0 0 3px rgba(3, 122, 155, 0.2)"
                                    />
                                    <Text
                                      fontSize="12px"
                                      fontWeight="600"
                                      color="#037A9B"
                                      textTransform="uppercase"
                                      letterSpacing="0.5px"
                                    >
                                      Included in Report
                                    </Text>
                                  </Flex>
                                )} */}
                              </Flex>
                            </Flex>
                          </Box>
                        );
                      })}
                    </VStack>


                  </VStack>
                )}
              </>

            )}
          </Box>
        )}
      </Box>

      {/* Feedback Dialog Modal */}
      <Modal isOpen={isFeedbackOpen} onClose={onFeedbackClose} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" p={2}>

          <ModalHeader fontSize="16px" fontWeight="700" color="#1A1A1A" pb={2}>
            {feedbackData.isRemoving ? 'Remove Feedback' : 'Provide Feedback'}
          </ModalHeader>

          <ModalBody>
            {feedbackData.isRemoving ? (
              <Text fontSize="14px" color="#6D6D6D">
                Are you sure you want to remove your feedback?
              </Text>
            ) : (
              <>
                <Text fontSize="12px" fontWeight="600" color="#9E9E9E"
                  textTransform="uppercase" letterSpacing="0.5px" mb={2}>
                  Selected feedback
                </Text>
                <Flex
                  align="center"
                  gap={2}
                  mb={4}
                  bg={feedbackData.feedbackType === 'positive' ? '#F6FFF6' : '#FFF5F5'}
                  border={`1px solid ${feedbackData.feedbackType === 'positive' ? '#C3E6C3' : '#FCCACA'}`}
                  borderRadius="10px"
                  px={3}
                  py={2}
                >
                  {feedbackData.feedbackType === 'positive' ? (
                    <>
                      <ThumbsUp size={18} color="#4CAF50" />
                      <Text fontSize="13px" fontWeight="600" color="#4CAF50">Thumbs Up</Text>
                    </>
                  ) : (
                    <>
                      <ThumbsDown size={18} color="#E53E3E" />
                      <Text fontSize="13px" fontWeight="600" color="#E53E3E">Thumbs Down</Text>
                    </>
                  )}
                </Flex>
              </>
            )}

            {!feedbackData.isRemoving && (
              <>
                <Text fontSize="13px" color="#6D6D6D" mb={2}>
                  Additional comments (optional)
                </Text>
                <Textarea
                  value={feedbackData.comment}
                  onChange={(e) => setFeedbackData({ ...feedbackData, comment: e.target.value })}
                  placeholder="Share your thoughts..."
                  rows={4}
                  borderRadius="12px"
                  borderColor="#E0E0E0"
                  fontSize="13px"
                  resize="none"
                  _focus={{ borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }}
                  _placeholder={{ color: '#ABABAB' }}
                />
              </>
            )}
          </ModalBody>

          <ModalFooter gap={2} pt={3}>
            <Button
              variant="ghost"
              onClick={onFeedbackClose}
              borderRadius="10px"
              fontSize="14px"
              color="#6D6D6D"
              _hover={{ bg: '#F5F5F5' }}
            >
              Cancel
            </Button>
            <Button
              bg={feedbackData.isRemoving ? '#E53E3E' : '#CF008A'}
              color="white"
              onClick={handleFeedbackSubmit}
              borderRadius="10px"
              fontSize="14px"
              fontWeight="600"
              _hover={{ bg: feedbackData.isRemoving ? '#C53030' : '#D933A1' }}
            >
              {feedbackData.isRemoving ? 'Remove' : 'Submit'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      {/* Suggestions Dialog */}
      <SuggestionsDialog
        isOpen={isSuggestionDialogOpen}
        onClose={() => {
          setSuggestionDialogOpen(false);
          setReviewError('');
        }}
        suggestions={isReviewLoading ? {} : extractedSuggestions}
        initialSelected={selectedSuggestions || []}
        isLoading={isReviewLoading || extractionState === 'loading'}
        errorMessage={reviewError}
        onRetry={handleReviewSuggestionsClick}
        onConfirm={(checkedKeys) => {
          // Close the dialog
          setSuggestionDialogOpen(false);
          setReviewError('');
          // Save the selections first
          onConfirmSelection(turnId, checkedKeys);

          // Trigger report generation logic
          if (checkedKeys.length === 0) {
            // No selections - show confirmation dialog
            setConfirmDialogOpen(true);
          } else {
            // Has selections - generate report directly
            onGenerateReport(turnId, checkedKeys);
          }
        }}
      />

      {/* Confirmation Dialog for No Selections */}
      <Modal
        isOpen={isConfirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent borderRadius="12px">
          <ModalHeader fontSize="16px" fontWeight="600" color="#242424">
            Confirm Report Generation
          </ModalHeader>
          <ModalBody fontSize="14px" color="#666">
            No optimizations have been selected. Do you confirm to generate report?
          </ModalBody>
          <ModalFooter gap="12px">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDialogOpen(false)}
              borderRadius="8px"
            >
              No
            </Button>
            <Button
              size="sm"
              bg="#CF008A"
              color="#FFFFFF"
              onClick={() => {
                setConfirmDialogOpen(false);
                onGenerateReport(turnId, []);
              }}
              borderRadius="8px"
              _hover={{ bg: "#B0007A" }}
            >
              Yes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
